import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Bookmark as BookmarkIcon, Clock } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import MathRenderer from '@/components/MathRenderer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bookmarks() {
  const { getAuthHeaders } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/questions/bookmarks`, {
        headers: getAuthHeaders()
      });
      setBookmarks(response.data);
    } catch (error) {
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading bookmarks...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div data-testid="bookmarks-header" className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Bookmarks</h1>
          <p className="text-slate-600">Your saved answers for quick revision</p>
        </div>

        {bookmarks.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <BookmarkIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No bookmarks yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookmarks.map((bookmark) => (
              <div
                key={bookmark.id}
                data-testid={`bookmark-item-${bookmark.id}`}
                className="bg-white rounded-lg border border-slate-200 p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex space-x-2">
                    <span className="marks-badge bg-yellow-50 text-yellow-700 px-2 py-1 rounded">
                      {bookmark.marks}M
                    </span>
                    <span className="marks-badge bg-slate-100 text-slate-700 px-2 py-1 rounded">
                      {bookmark.subject}
                    </span>
                  </div>
                  <span className="text-sm text-slate-500 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {new Date(bookmark.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-900 font-medium mb-3">{bookmark.question_text}</p>
                <div className="p-4 bg-blue-50/30 rounded-md border-l-4 border-blue-900">
                  <div className="text-slate-900">
                    <MathRenderer content={bookmark.answer} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
